/**
 * The bracket that goes out with an order (SPEC §9.4, ADR-0017, ADR-0025): from the selected
 * preset, or from the order ticket's one-off Stop Loss / Take Profit. Pure; only type-imports
 * generated code, so it runs under `node --test`.
 *
 * - **Quantity.** A preset is written for its own quantity. For a different order quantity the
 *   legs are scaled in proportion (largest remainder, earlier legs first on ties); a leg that
 *   scales to 0 contracts is dropped. The legs always add up to the order quantity.
 * - **$ amounts (Q-B1, per SPEC §9.4).** A leg entered in $ keeps its amount; at submit its ticks
 *   are re-derived for the leg's actual quantity — stops floor (the loss never exceeds the
 *   amount), targets ceil — and a stop that floors to 0 ticks refuses the order ("Risk $ is below
 *   1 tick at this quantity").
 * - **Template units (ADR-0049).** A points / $-per-contract template is first resolved for the
 *   contract traded (`resolvePreset`): from live reference data only, every offset re-derived
 *   with the rounding the bracket control showed, refused when it cannot be. A leg may carry a
 *   target only or a stop only (0 on the wire = none).
 * - The gateway validates everything again (1–3 legs, Σ qty = order qty, offsets 1–4,000 ticks).
 */
import type { BracketLeg as PbBracketLeg, BracketSpec } from "../gen/helm/v1/trading_pb";
import {
  centsToInputString,
  dollarsToTicks,
  resolvePreset,
  type BracketPreset,
  type ContractFacts,
  type FieldConversion,
} from "./model.ts";

export type OrderBracketLeg = { qty: number; targetTicks: number | null; stopTicks: number | null };

export type OrderBracket = {
  legs: OrderBracketLeg[];
  breakeven: { triggerTicks: number; offsetTicks: number } | null;
  trail: { triggerTicks: number; distanceTicks: number; stepTicks: number } | null;
  /** Some offset was re-derived from a $ amount for this quantity. */
  dollarMode: boolean;
  /** The preset's name (null for the ticket's one-off bracket). */
  name: string | null;
};

/** `conversions`: how a points / $ template's typed values became these ticks (empty for ticks). */
export type BracketResult =
  { ok: true; bracket: OrderBracket; conversions: FieldConversion[] } | { ok: false; message: string };

/**
 * Split `qty` contracts over legs written as `legQtys` (summing to their preset quantity) in
 * proportion. Largest remainder; ties go to the earlier leg. Entries may be 0.
 */
export function scaleLegQty(legQtys: readonly number[], qty: number): number[] {
  const total = legQtys.reduce((a, b) => a + b, 0);
  if (total <= 0 || qty <= 0) return legQtys.map(() => 0);
  if (total === qty) return legQtys.slice();
  const base = legQtys.map((q) => Math.floor((qty * q) / total));
  const rem = legQtys.map((q, i) => ({ i, r: (qty * q) % total }));
  let left = qty - base.reduce((a, b) => a + b, 0);
  rem.sort((a, b) => b.r - a.r || a.i - b.i);
  for (const { i } of rem) {
    if (left <= 0) break;
    base[i] = (base[i] ?? 0) + 1;
    left -= 1;
  }
  return base;
}

/** The bracket of an order of `qty` contracts from preset `p`. */
export function bracketForPreset(
  preset: BracketPreset,
  qty: number,
  facts: ContractFacts | null,
): BracketResult {
  if (!Number.isSafeInteger(qty) || qty < 1) return { ok: false, message: "Quantity must be at least 1" };
  const resolved = resolvePreset(preset, facts);
  if (!resolved.ok) return resolved;
  const p = resolved.preset;
  const shares = scaleLegQty(
    p.legs.map((l) => l.qty),
    qty,
  );
  const legs: OrderBracketLeg[] = [];
  let dollarMode = p.unit === "dollars";
  for (let i = 0; i < p.legs.length; i++) {
    const l = p.legs[i];
    const share = shares[i] ?? 0;
    if (l === undefined || share === 0) continue;
    const ticks = (
      cents: number | undefined,
      fixed: number | null,
      role: "stop" | "target",
    ): number | string | null => {
      // Older $ amounts across the leg's quantity (Q-B1) exist on ticks templates only.
      if (cents === undefined || fixed === null || p.unit !== "ticks") return fixed;
      if (facts === null) return "Tick value unknown: the $ bracket cannot be sized";
      dollarMode = true;
      const r = dollarsToTicks(centsToInputString(cents), share, facts, role);
      return r.ok ? r.ticks : r.message;
    };
    const target = ticks(l.targetCents, l.targetTicks, "target");
    const stop = ticks(l.stopCents, l.stopTicks, "stop");
    if (typeof stop === "string") return { ok: false, message: `Leg ${String(i + 1)} stop: ${stop}` };
    if (typeof target === "string") return { ok: false, message: `Leg ${String(i + 1)} target: ${target}` };
    legs.push({ qty: share, targetTicks: target, stopTicks: stop });
  }
  const be = p.breakeven;
  const tr = p.trail;
  return {
    ok: true,
    bracket: {
      legs,
      // Ticks only: the typed values stay with the template.
      breakeven: be ? { triggerTicks: be.triggerTicks, offsetTicks: be.offsetTicks } : null,
      trail: tr
        ? { triggerTicks: tr.triggerTicks, distanceTicks: tr.distanceTicks, stepTicks: tr.stepTicks }
        : null,
      dollarMode,
      name: p.name,
    },
    conversions: resolved.conversions,
  };
}

/**
 * The order ticket's one-off protection (SPEC §9.2 Order mode): a stop and/or a target in ticks
 * from the entry, one leg for the whole quantity. "Trail" on the stop trails it from the first
 * tick of profit at its own distance. null when neither is on.
 */
export function ticketBracket(
  stopTicks: number | null,
  targetTicks: number | null,
  trailStop: boolean,
  qty: number,
): OrderBracket | null {
  if (stopTicks === null && targetTicks === null) return null;
  return {
    legs: [{ qty, targetTicks, stopTicks }],
    breakeven: null,
    trail:
      trailStop && stopTicks !== null ? { triggerTicks: 1, distanceTicks: stopTicks, stepTicks: 1 } : null,
    dollarMode: false,
    name: null,
  };
}

/** `helm.v1.BracketSpec` for the order message (0 = none for a leg's target or stop). */
export function bracketSpecMessage(b: OrderBracket): BracketSpec {
  const legs: PbBracketLeg[] = b.legs.map((l) => ({
    $typeName: "helm.v1.BracketLeg",
    targetTicks: l.targetTicks ?? 0,
    qty: l.qty,
    stopTicks: l.stopTicks ?? 0,
  }));
  return {
    $typeName: "helm.v1.BracketSpec",
    legs,
    breakevenTriggerTicks: b.breakeven?.triggerTicks ?? 0,
    breakevenOffsetTicks: b.breakeven?.offsetTicks ?? 0,
    trailTriggerTicks: b.trail?.triggerTicks ?? 0,
    trailDistanceTicks: b.trail?.distanceTicks ?? 0,
    trailStepTicks: b.trail?.stepTicks ?? 0,
    dollarMode: b.dollarMode,
  };
}

const MINUS = "−";
const uniq = (xs: number[]): number[] => [...new Set(xs)];

/**
 * "TP +16/+40t · SL −16t · BE · Trail" for the confirm popover and the ticket's words. A side no
 * leg places says so ("no SL"), so a target-only bracket never reads as protected.
 */
export function bracketWords(b: OrderBracket): string {
  const tp = uniq(b.legs.flatMap((l) => (l.targetTicks === null ? [] : [l.targetTicks])));
  const sl = uniq(b.legs.flatMap((l) => (l.stopTicks === null ? [] : [l.stopTicks])));
  const parts: string[] = [];
  parts.push(tp.length > 0 ? `TP ${tp.map((t) => `+${String(t)}`).join("/")}t` : "no TP");
  parts.push(sl.length > 0 ? `SL ${sl.map((t) => `${MINUS}${String(t)}`).join("/")}t` : "no SL");
  if (b.legs.length > 1) parts.push(`${String(b.legs.length)} legs`);
  if (b.breakeven) parts.push("BE");
  if (b.trail) parts.push("Trail");
  return parts.join(" · ");
}
